/**
 * Admin User Verification – same structure as Admin Payments: hero, stats bento,
 * filters, user list, pagination, verify/details modals.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import Avatar from '../../components/common/Avatar';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

function normalizeUser(u) {
  if (!u) return null;
  return {
    ...u,
    fullName: u.full_name ?? u.fullName ?? u.name,
    profilePhotoUrl: u.profile_photo_url ?? u.profilePhotoUrl ?? u.photoUrl,
    createdAt: u.created_at ?? u.createdAt,
    verificationLevel: u.verification_level ?? u.verificationLevel ?? 0,
    email: u.email ?? u.email_address,
    phone: u.phone ?? u.phone_number,
  };
}

const AdminUserVerification = () => {
  const navigate = useNavigate();
  const {
    listUsers,
    verifyUser,
    getUserDetails,
    loading,
  } = useAdmin();
  const { showError, showSuccess } = useToast();
  const { formatDate, language } = useI18n();

  const [usersList, setUsersList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [statusTab, setStatusTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [verifyData, setVerifyData] = useState({ verificationLevel: 2, notes: '' });
  const statsRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const filters = {
          search: appliedSearch || undefined,
          verificationLevel: levelFilter ? parseInt(levelFilter, 10) : undefined,
          page: 1,
          limit: 50,
        };
        const response = await listUsers(filters);
        const data = response?.data ?? response;
        const list = data?.users ?? (Array.isArray(data) ? data : []);
        setUsersList(Array.isArray(list) ? list : []);
        if (data?.pagination) setPagination(data.pagination);
      } catch (err) {
        showError(
          err?.message ||
            (language === 'fr' ? 'Échec du chargement des utilisateurs' : 'Failed to load users')
        );
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [listUsers, appliedSearch, levelFilter, showError, language]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setStatsInView(true)),
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getVerificationStatus = (user) => {
    const level = user?.verification_level ?? user?.verificationLevel ?? 0;
    if (level >= 2) return 'verified';
    if (level === 1) return 'partial';
    if (user?.kyc_status === 'pending' || user?.kycStatus === 'pending') return 'pending';
    return 'unverified';
  };

  const getStatusVariant = (status) => {
    const v = { verified: 'success', partial: 'info', pending: 'warning', unverified: 'default' };
    return v[status] || 'default';
  };

  const getStatusLabel = (user) => {
    const status = getVerificationStatus(user);
    if (status === 'verified') return language === 'fr' ? 'KYC vérifié' : 'KYC Verified';
    if (status === 'partial') return language === 'fr' ? 'Email vérifié' : 'Email Verified';
    if (status === 'pending') return language === 'fr' ? 'En attente' : 'Pending';
    return language === 'fr' ? 'Non vérifié' : 'Unverified';
  };

  const filtered = usersList.filter((u) => {
    if (statusTab === 'all') return true;
    return getVerificationStatus(u) === statusTab;
  });

  const stats = {
    total: usersList.length,
    pending: usersList.filter((u) => getVerificationStatus(u) === 'pending').length,
    verified: usersList.filter((u) => getVerificationStatus(u) === 'verified').length,
    unverified: usersList.filter((u) => getVerificationStatus(u) === 'unverified').length,
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchQuery.trim());
  };

  const handleClear = () => {
    setSearchQuery('');
    setAppliedSearch('');
    setLevelFilter('');
  };

  const handleVerifyClick = (user) => {
    setSelectedUser(user);
    setVerifyData({
      verificationLevel: user?.verification_level ?? user?.verificationLevel ?? 2,
      notes: '',
    });
    setShowVerifyModal(true);
  };

  const handleVerify = async () => {
    if (!selectedUser) return;
    try {
      await verifyUser(selectedUser.id, verifyData);
      showSuccess(
        language === 'fr' ? 'Utilisateur vérifié.' : 'User verified.'
      );
      setShowVerifyModal(false);
      setSelectedUser(null);
      const response = await listUsers({
        search: appliedSearch || undefined,
        verificationLevel: levelFilter ? parseInt(levelFilter, 10) : undefined,
      });
      const data = response?.data ?? response;
      const list = data?.users ?? (Array.isArray(data) ? data : []);
      setUsersList(Array.isArray(list) ? list : []);
    } catch (err) {
      showError(
        err?.message ||
          (language === 'fr' ? 'Échec de la vérification' : 'Failed to verify')
      );
    }
  };

  const handleViewDetails = async (user) => {
    try {
      setSelectedUser(user);
      const details = await getUserDetails(user.id);
      const d = details?.user ?? details?.data ?? details ?? user;
      setUserDetails(d);
      setShowDetailsModal(true);
    } catch (err) {
      showError(
        err?.message ||
          (language === 'fr' ? 'Échec du chargement des détails' : 'Failed to load details')
      );
    }
  };

  const levelOptions = [
    { value: '', label: language === 'fr' ? 'Tous les niveaux' : 'All Levels' },
    { value: '0', label: language === 'fr' ? 'Niveau 0' : 'Level 0' },
    { value: '1', label: language === 'fr' ? 'Niveau 1' : 'Level 1' },
    { value: '2', label: language === 'fr' ? 'Niveau 2 (KYC)' : 'Level 2 (KYC)' },
  ];

  const statusTabs = [
    { id: 'all', label: language === 'fr' ? 'Tous' : 'All', icon: '👥' },
    { id: 'pending', label: language === 'fr' ? 'En attente' : 'Pending', icon: '⏳' },
    { id: 'verified', label: language === 'fr' ? 'Vérifiés' : 'Verified', icon: '✅' },
    { id: 'unverified', label: language === 'fr' ? 'Non vérifiés' : 'Unverified', icon: '🔓' },
  ];

  if (isLoading && usersList.length === 0) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des vérifications...' : 'Loading verifications...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              🆔 {language === 'fr' ? 'Vérification des utilisateurs' : 'User Verification'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez les vérifications KYC et les niveaux des utilisateurs'
                : 'Manage KYC verifications and user verification levels'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                { icon: '👥', label: language === 'fr' ? 'Total' : 'Total', value: stats.total },
                { icon: '⏳', label: language === 'fr' ? 'En attente' : 'Pending', value: stats.pending },
                { icon: '✅', label: language === 'fr' ? 'Vérifiés' : 'Verified', value: stats.verified },
                { icon: '🔓', label: language === 'fr' ? 'Non vérifiés' : 'Unverified', value: stats.unverified },
              ].map((s, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{s.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{s.value}</div>
                    <div className="dashboard-wt-stat-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters & list */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form onSubmit={handleSearch} className="transactions-wt-form">
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          language === 'fr'
                            ? 'Nom, email, téléphone...'
                            : 'Name, email, phone...'
                        }
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={levelFilter}
                        onChange={(e) => setLevelFilter(e.target.value || '')}
                        options={levelOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(appliedSearch || levelFilter) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={handleClear}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Status tabs */}
            <div className="dashboard-wt-card p-2 mb-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              <div className="flex flex-wrap gap-2">
                {statusTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setStatusTab(t.id)}
                    className={`flex items-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${
                      statusTab === t.id
                        ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.3)]'
                        : 'text-[#a3a3a3] hover:bg-[rgba(255,255,255,0.05)] border border-transparent'
                    }`}
                  >
                    <span>{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">🆔</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucun utilisateur' : 'No users found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun utilisateur ne correspond aux filtres'
                    : 'No users match your filters'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary dashboard-wt-btn-large"
                  onClick={handleClear}
                >
                  {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <div className="dashboard-wt-transactions transactions-wt-list">
                {filtered.map((user, index) => {
                  const u = normalizeUser(user);
                  const status = getVerificationStatus(user);
                  return (
                    <div
                      key={user.id}
                      className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="dashboard-wt-card-content">
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/80 to-green-600/80 flex items-center justify-center overflow-hidden flex-shrink-0">
                            <Avatar
                              src={u.profilePhotoUrl}
                              name={u.fullName || 'U'}
                              size="md"
                              variant="circle"
                              className="!w-12 !h-12"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-bold text-[#e5e5e5] truncate">
                                {u.fullName || (language === 'fr' ? 'Utilisateur' : 'User')}
                              </h3>
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${
                                  status === 'verified'
                                    ? 'bg-green-500/20 text-green-400'
                                    : status === 'pending'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : status === 'partial'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-[#2a2a2a] text-[#a3a3a3]'
                                }`}
                              >
                                {getStatusLabel(user)}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                              {u.email && <span>{u.email}</span>}
                              {u.phone && (
                                <>
                                  {u.email && <span>·</span>}
                                  <span>{u.phone}</span>
                                </>
                              )}
                              <span>·</span>
                              <span>
                                {language === 'fr' ? 'Niveau' : 'Level'}{' '}
                                {(u.verification_level ?? u.verificationLevel ?? 0)}/2
                              </span>
                              {u.createdAt && (
                                <>
                                  <span>·</span>
                                  <span>{formatDate(u.createdAt)}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 flex-shrink-0">
                            <button
                              type="button"
                              className="dashboard-wt-btn-secondary"
                              onClick={() => handleViewDetails(user)}
                            >
                              {language === 'fr' ? 'Détails' : 'Details'}
                            </button>
                            {(status === 'pending' || status === 'unverified' || status === 'partial') && (
                              <button
                                type="button"
                                className="dashboard-wt-btn-primary"
                                onClick={() => handleVerifyClick(user)}
                              >
                                ✅ {language === 'fr' ? 'Vérifier' : 'Verify'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Verify modal */}
      <Modal
        isOpen={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false);
          setSelectedUser(null);
          setVerifyData({ verificationLevel: 2, notes: '' });
        }}
        title={language === 'fr' ? "Vérifier l'utilisateur" : 'Verify User'}
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="p-3 rounded-xl bg-[var(--wt-black-2,#111)] border border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center gap-3">
                <Avatar
                  src={selectedUser.profile_photo_url ?? selectedUser.profilePhotoUrl}
                  name={selectedUser.full_name ?? selectedUser.fullName ?? 'U'}
                  size="sm"
                />
                <div>
                  <p className="font-bold text-[#e5e5e5]">
                    {selectedUser.full_name ?? selectedUser.fullName ?? selectedUser.name}
                  </p>
                  {selectedUser.email && (
                    <p className="text-sm text-[#a3a3a3]">{selectedUser.email}</p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
                {language === 'fr' ? 'Niveau de vérification' : 'Verification level'}
              </label>
              <Select
                value={String(verifyData.verificationLevel)}
                onChange={(e) =>
                  setVerifyData((prev) => ({
                    ...prev,
                    verificationLevel: parseInt(e.target.value, 10),
                  }))
                }
                options={[
                  { value: '0', label: language === 'fr' ? 'Niveau 0 - Téléphone' : 'Level 0 - Phone' },
                  { value: '1', label: language === 'fr' ? 'Niveau 1 - Email' : 'Level 1 - Email' },
                  { value: '2', label: language === 'fr' ? 'Niveau 2 - KYC' : 'Level 2 - KYC' },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
                {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
              </label>
              <Textarea
                value={verifyData.notes}
                onChange={(e) =>
                  setVerifyData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={
                  language === 'fr'
                    ? 'Notes sur cette vérification...'
                    : 'Notes about this verification...'
                }
                rows={3}
                className="!bg-[var(--wt-black-2,#111)] !border-[rgba(255,255,255,0.12)] !text-[#fafafa]"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowVerifyModal(false);
                  setSelectedUser(null);
                  setVerifyData({ verificationLevel: 2, notes: '' });
                }}
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                variant="gradient"
                fullWidth
                onClick={handleVerify}
                loading={loading}
                className="bg-gradient-to-r from-green-600 to-emerald-600"
              >
                ✅ {language === 'fr' ? 'Vérifier' : 'Verify'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Details modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedUser(null);
          setUserDetails(null);
        }}
        title={language === 'fr' ? "Détails de l'utilisateur" : 'User Details'}
        size="lg"
      >
        {userDetails && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Avatar
                src={userDetails.profile_photo_url ?? userDetails.profilePhotoUrl}
                name={userDetails.full_name ?? userDetails.fullName ?? 'U'}
                size="xl"
                variant="circle"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-[#e5e5e5]">
                  {userDetails.full_name ?? userDetails.fullName ?? userDetails.name}
                </h3>
                {userDetails.email && (
                  <p className="text-[#a3a3a3]">{userDetails.email}</p>
                )}
                {userDetails.phone && (
                  <p className="text-sm text-[#737373]">{userDetails.phone}</p>
                )}
              </div>
            </div>
            <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              <h4 className="font-semibold text-[#e5e5e5] mb-3">
                {language === 'fr' ? 'Statut de vérification' : 'Verification status'}
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#a3a3a3]">{language === 'fr' ? 'Niveau' : 'Level'}</span>
                  <span className="text-[#e5e5e5] font-medium">
                    {(userDetails.verification_level ?? userDetails.verificationLevel ?? 0)}/2
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#a3a3a3]">{getStatusLabel(userDetails)}</span>
                </div>
                {(userDetails.created_at ?? userDetails.createdAt) && (
                  <div className="flex justify-between">
                    <span className="text-[#a3a3a3]">
                      {language === 'fr' ? 'Membre depuis' : 'Member since'}
                    </span>
                    <span className="text-[#e5e5e5]">
                      {formatDate(userDetails.created_at ?? userDetails.createdAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {(userDetails.kyc_documents ?? userDetails.kycDocuments) && (
              <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <h4 className="font-semibold text-[#e5e5e5] mb-3">
                  {language === 'fr' ? 'Documents KYC' : 'KYC documents'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(userDetails.kyc_documents?.document_front_url ?? userDetails.kycDocuments?.documentFrontUrl) && (
                    <div>
                      <p className="text-xs text-[#a3a3a3] mb-1">
                        {language === 'fr' ? 'Recto' : 'Front'}
                      </p>
                      <img
                        src={
                          userDetails.kyc_documents?.document_front_url ??
                          userDetails.kycDocuments?.documentFrontUrl
                        }
                        alt="ID front"
                        className="w-full rounded-lg border border-[rgba(255,255,255,0.12)] max-h-40 object-cover"
                      />
                    </div>
                  )}
                  {(userDetails.kyc_documents?.document_back_url ?? userDetails.kycDocuments?.documentBackUrl) && (
                    <div>
                      <p className="text-xs text-[#a3a3a3] mb-1">
                        {language === 'fr' ? 'Verso' : 'Back'}
                      </p>
                      <img
                        src={
                          userDetails.kyc_documents?.document_back_url ??
                          userDetails.kycDocuments?.documentBackUrl
                        }
                        alt="ID back"
                        className="w-full rounded-lg border border-[rgba(255,255,255,0.12)] max-h-40 object-cover"
                      />
                    </div>
                  )}
                  {(userDetails.kyc_documents?.selfie_url ?? userDetails.kycDocuments?.selfieUrl) && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-[#a3a3a3] mb-1">
                        {language === 'fr' ? 'Selfie' : 'Selfie'}
                      </p>
                      <img
                        src={
                          userDetails.kyc_documents?.selfie_url ??
                          userDetails.kycDocuments?.selfieUrl
                        }
                        alt="Selfie"
                        className="w-full rounded-lg border border-[rgba(255,255,255,0.12)] max-h-40 object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              {getVerificationStatus(userDetails) !== 'verified' && (
                <Button
                  variant="gradient"
                  fullWidth
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleVerifyClick(userDetails);
                  }}
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  ✅ {language === 'fr' ? 'Vérifier' : 'Verify'}
                </Button>
              )}
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedUser(null);
                  setUserDetails(null);
                }}
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default AdminUserVerification;
